import React from "react"
import { TIconNameV2, TIconPathsV2 } from "./TIconPathsV2"

/**
 * Lucide-style stroke 렌더링 아이콘 이름 set — TIcon 내부 로컬 상수.
 * STROKE_ICONS_V2 export 를 cross-module import 시 production 빌드에서
 * tree-shaking / module 평가 순서 회귀 (closed-fist 처럼 fill 렌더되는 회귀) 차단 위해 inline.
 * 새 stroke 아이콘 추가 시 TIconPathsV2 의 path + 이 set 양쪽 모두 갱신.
 */
const STROKE_ICON_NAMES = new Set<TIconNameV2>(["arrowsAll", "hand", "lasso", "imagePlus", "chatAttach", "cut"])

/**
 * @description SVG 아이콘 렌더러. TIconNameV2 타입의 아이콘 세트 사용.
 * 두 가지 렌더 모드:
 * - 기본 (fill, viewBox 32x32): TIconPathsV2 의 사내 fill silhouette
 * - stroke (viewBox 24x24, stroke 2 round): STROKE_ICON_NAMES 에 포함된 Lucide-style 아이콘
 * @example
 * <TIcon name="search" size={24} />
 * <TIcon name="close" size={16} color="text-secondary" />
 */
interface TIconProps {
	/** 추가 CSS 클래스 */
	className?: string
	/** 아이콘 이름 (예: "search", "close", "edit", "delete", "more_vert", "check", "arrow_back") */
	name: TIconNameV2
	/**
	 * 아이콘 크기 (px)
	 * @default 24
	 */
	size?: number
	/** CSS 색상값 (예: "var(--text)", "var(--text-secondary)", "#333") */
	color?: string
	/** 드롭 섀도우 적용 여부 */
	shadow?: boolean
}

export const TIcon = React.memo((props: TIconProps) => {
	const { className, name, size = 24, color, shadow } = props
	const isStroke = STROKE_ICON_NAMES.has(name)
	const colorValue = color ? `var(--${color})` : "currentColor"

	return (
		<svg
			suppressHydrationWarning
			className={className}
			width={size}
			height={size}
			viewBox={isStroke ? "0 0 24 24" : "0 0 32 32"}
			fill={isStroke ? "none" : colorValue}
			stroke={isStroke ? colorValue : undefined}
			strokeWidth={isStroke ? 2 : undefined}
			strokeLinecap={isStroke ? "round" : undefined}
			strokeLinejoin={isStroke ? "round" : undefined}
			fillRule={isStroke ? undefined : "evenodd"}
			clipRule={isStroke ? undefined : "evenodd"}
			shapeRendering="geometricPrecision"
			aria-hidden="true"
			style={{
				filter: shadow ? "drop-shadow( 0px 0px 2px rgba(0, 0, 0, 0.25))" : undefined,
				// stroke 모드 inline fill — button.scss 의 `svg { @apply fill-current }` 가
				// presentation attribute (fill="none") 를 덮어쓰는 회귀 차단. inline style 가 CSS rule 보다 강함.
				fill: isStroke ? "none" : undefined
			}}
		>
			<path d={TIconPathsV2[name]} />
		</svg>
	)
})

TIcon.displayName = "TIcon"
